import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import { touchTargetSx } from "_shared/_views/touch-target";
import { type NameState, targetStateText } from "permissions/_views/targets/permission-target-state";
import { accessibleNames, targetStates } from "permissions/_views/wording/permission-wording";
import { useId, useState } from "react";

/**
 * The record a per-record row is on, or why the row cannot name it (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "Target and person states").
 *
 * - **Found**: the record's name.
 * - **Missing**: "Conversation no longer exists", and Remove, since the grant opens nothing.
 * - **Unauthorized**: "No access to this conversation". The id is behind an "Id" chip: pressing it
 *   shows the id in the row, where it can be selected and copied, so support can be told which record
 *   it is; hovering or focusing it shows the id in a tooltip, and a screen reader reads it with the chip.
 * - **Failed**: "Couldn't load this conversation", and Retry.
 * - **Loading**: skeleton text.
 */
export default function PermissionTargetCell(
  props: Readonly<{
    state: NameState;
    /** The kind's target noun from the kinds endpoint: "conversation". */
    noun: string;
    targetId: string;
    /** The kind and person the row is about, which the Retry and Remove names end with. */
    kindName: string;
    personName: string;
    onRetry?: () => void;
    /** Offered when the record no longer exists. */
    onRemove?: () => void;
    pending?: boolean;
  }>,
) {
  const { state, noun, targetId, kindName, personName, onRetry, onRemove, pending } = props;
  const idTextId = useId();
  const [showId, setShowId] = useState(false);

  if (state.state === "loading") return <Skeleton variant="text" width="60%" aria-label={`Loading the ${noun}`} />;
  if (state.state === "found") {
    return (
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {state.name}
      </Typography>
    );
  }

  const text = targetStateText(state, noun);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <ErrorOutlineIcon fontSize="inherit" aria-hidden />
        {text}
      </Typography>
      {state.state === "unauthorized" && targetId && (
        <>
          <Tooltip title={targetStates.id(noun, targetId)}>
            <Chip
              size="small"
              variant="outlined"
              label="Id"
              clickable
              sx={touchTargetSx()}
              aria-label={accessibleNames.targetId(noun)}
              aria-describedby={idTextId}
              aria-expanded={showId}
              onClick={() => setShowId(!showId)}
            />
          </Tooltip>
          {showId ? (
            <Typography id={idTextId} variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", userSelect: "all" }}>
              {targetId}
            </Typography>
          ) : (
            <span id={idTextId} style={visuallyHidden}>
              {targetId}
            </span>
          )}
        </>
      )}
      {state.state === "missing" && onRemove && (
        <Button size="small" color="error" sx={touchTargetSx()} disabled={pending} onClick={onRemove} aria-label={accessibleNames.removeTarget(kindName, personName)}>
          Remove
        </Button>
      )}
      {state.state === "failed" && onRetry && (
        <Button size="small" sx={touchTargetSx()} onClick={onRetry} aria-label={accessibleNames.retryTarget(noun, kindName, personName)}>
          Retry
        </Button>
      )}
    </Box>
  );
}
